import React, { 
  useEffect, 
  useState, 
  useMemo, 
  useRef, 
  useCallback,
  useLayoutEffect 
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import useHouseholdHoldings from "../../../hooks/useHouseholdHoldings";
import AssetAllocationChart from "../../../components/allocations/AssetAllocationChart";
import SectorAllocationChart from "../../../components/allocations/SectorAllocationChart";
import PerformanceChart from "../../../components/allocations/PerformanceChart";
import HoldingsTable from "../../../components/allocations/HoldingsTable";
import SubPageLayout from "../../../components/shared/SubPageLayout";
import SymbolDetailModal from "../../../components/allocations/SymbolDetailModal";
import { useDemo } from "../../../contexts/DemoContext";
import { MODAL_TYPES } from "../../../demo/annualReviewDemoLogic";
import SidebarTemplate from "../../../components/shared/SidebarTemplate";
import { useTranscription } from "../../../contexts/TranscriptionContext";
import { useTheme } from "../../../contexts/ThemeContext";
import { ThemeProvider } from "styled-components";
import { CARD_BASE, CHART_SIZES, TABLE_VARIANTS, TABLE_BASE } from "../../../constants";
import { getRangeCutoff } from "../../../utils/chartUtils";
import styled from "styled-components";

// Add styled component for the sidebar wrapper
const SidebarWrapper = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  z-index: 100001;
  isolation: isolate;
  pointer-events: ${props => props.$demoActive ? 'none' : 'auto'};

  & > * {
    height: 100vh;
    box-shadow: ${props => props.theme.isDarkMode ? 'none' : '-2px 0 10px rgba(0, 0, 0, 0.1)'};
  }

  /* Ensure the sidebar stays clear of any overlays */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
  }
`;

// Safety switch for legacy restore logic
const SKIP_LEGACY_RESTORE = localStorage.getItem("skipLegacyRestore") === "true";

// Demo step constants
const DEMO_STEPS = {
  DEMO_ASSET_ALLOCATION_OVERVIEW: 'DEMO_ASSET_ALLOCATION_OVERVIEW',
  // Add other demo steps as needed
};

function allocationToChartData(allocationMap, palette) {
  const labels = Object.keys(allocationMap || {});
  const data = Object.values(allocationMap || {});
  return {
    labels,
    datasets: [
      {
        data,
        backgroundColor: palette || [
          "#8B0000",
          "#4682B4",
          "#CD853F",
          "#6495ED",
          "#800080",
          "#90EE90",
          "#FFD700",
          "#FF69B4",
          "#A0522D",
          "#2E8B57",
        ],
      },
    ],
  };
}

function getAccountDisplayName(h) {
  if (!h) return "";
  const first = h.first_name || "";
  const last = h.last_name || "";
  const secFirst = h.secondary_first_name || "";
  const secLast = h.secondary_last_name || "";
  const accountType = h.account_type_name || "";
  const taxQual = h.tax_qualification_name || "";
  let name = `${first} ${last}`.trim();
  if (secFirst || secLast) {
    const secName = `${secFirst} ${secLast}`.trim();
    if (secName) name += ` & ${secName}`;
  }
  if (accountType) {
    if (taxQual && !accountType.toLowerCase().includes(taxQual.toLowerCase())) {
      name += ` ${accountType} (${taxQual})`;
    } else {
      name += ` ${accountType}`;
    }
  } else if (taxQual) {
    name += ` (${taxQual})`;
  }
  return name.trim();
}

// Add price map optimization helper
const buildPriceMap = (priceHistory, symbol) => {
  const map = new Map();
  if (!priceHistory[symbol]) return map;
  priceHistory[symbol].forEach(([date, price]) => {
    map.set(date, price);
  });
  return map;
};

const HouseholdDetailView = () => {
  const { householdId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { resumeDemo } = location.state || {};
  const { 
    isDemoMode, 
    demoStep,
    onAction,
    activeModal,
    activeSpotlight 
  } = useDemo();
  const { theme, isDarkMode } = useTheme();
  
  // Add ref for symbol detail modal
  const modalContainerRef = useRef(null);

  // Add state to track if we've already handled the resume
  const [hasResumed, setHasResumed] = useState(false);
  
  // Log component mount/unmount
  useEffect(() => {
    console.log('🏠 HouseholdDetailView mounted', {
      locationState: location.state,
      isDemoMode,
      demoStep,
      hasResumed
    });
    return () => {
      console.log('🏠 HouseholdDetailView unmounting');
      // Don't reset hasResumed on unmount anymore
    };
  }, []);

  // FIRST, grab the transcription methods
  const {
    transcript,
    setTranscript,
    setCurrentIndex,
    setIsRecording,
    setIsPaused,
    isRecording,
    isPaused,
    addTranscriptLine,
    currentIndex
  } = useTranscription();
  
  // State for UI controls
  const [isExpanded, setIsExpanded] = useState(location.state?.preserveSidebar ?? true);

  // Add demo lock state
  const [isDemoLocked, setIsDemoLocked] = useState(false);

  // Effect to sync demo lock state
  useEffect(() => {
    if (isDemoMode) {
      setIsDemoLocked(isRecording && !isPaused);
    } else {
      setIsDemoLocked(false);
    }
  }, [isDemoMode, isRecording, isPaused]);

  // ✅ FIRST: Create transcriptSlice for SidebarTemplate with safety checks
  const transcriptSlice = useMemo(() => {
    if (!transcript || currentIndex === undefined) {
      console.warn('⚠️ Missing transcript or currentIndex:', {
        hasTranscript: !!transcript,
        currentIndex
      });
      return [];
    }

    // Don't limit by transcript.length - show all entries up to currentIndex
    const safeIndex = Math.max(0, currentIndex + 1);
    console.log('🧾 Creating transcript slice:', {
      currentIndex,
      fullLength: transcript.length,
      willSliceTo: safeIndex,
      transcriptEntries: transcript.slice(0, safeIndex).map(entry => entry.time)
    });
    return transcript.slice(0, safeIndex);
  }, [transcript, currentIndex]);

  // ✅ THEN: Monitor transcript growth
  useEffect(() => {
    console.log('🧮 [HouseholdDetailView] Transcript stats:', {
      length: transcript?.length,
      currentIndex,
      sliceLength: transcriptSlice?.length,
      lastSliceEntry: transcriptSlice?.[transcriptSlice.length - 1],
      lastTranscriptEntry: transcript?.[transcript.length - 1]
    });
  }, [transcript, currentIndex, transcriptSlice]);

  // Effect to handle resumeDemo from location state
  useEffect(() => {
    const { resumeDemo } = location.state || {};
    if (!resumeDemo || hasResumed) return;
    
    console.log('🔄 Letting DemoContext handle resume action:', resumeDemo);
    
    // Let DemoContext handle the action
    onAction({
      type: 'RESUME_DEMO',
      payload: {
        transcript: location.state.transcript,
        currentIndex: location.state.currentIndex || 0,
        isExpanded: true
      }
    });
    setHasResumed(true);
    
    // Clean up resumeDemo from history
    const newState = { ...location.state };
    delete newState.resumeDemo;
    window.history.replaceState(newState, '', window.location.pathname);
  }, [location.state, onAction, hasResumed]);

  // Effect to update expanded state when location state changes
  useEffect(() => {
    console.log('🏠 HouseholdDetailView sidebar state:', {
      locationState: location.state,
      isDemoMode,
      isExpanded,
      preserveSidebar: location.state?.preserveSidebar,
      hasResumed
    });
    if (location.state?.preserveSidebar || isDemoMode) {
      setIsExpanded(true);
    }
  }, [location.state, isDemoMode]);

  // Effect to initialize transcript state from location state
  useEffect(() => {
    if (hasResumed || !location.state?.transcript) return;

    console.log('🎯 [HouseholdDetailView] First-time resume with transcript:', {
      transcriptLength: location.state.transcript?.length,
      lastEntry: location.state.transcript?.[location.state.transcript.length - 1],
      isRecording: location.state.isRecording,
      isPaused: location.state.isPaused,
      fullLocationState: location.state,
      currentIndex,
      hasResumed
    });

    // Let DemoContext handle all transcript state management
    onAction({ 
      type: 'RESUME_ALLOCATIONS', 
      payload: { 
        transcript: location.state.transcript,
        isRecording: location.state.isRecording,
        isPaused: location.state.isPaused
      }
    });
    setHasResumed(true);
  }, [location.state, onAction, currentIndex, hasResumed]);

  // Effect to force sidebar expansion in demo mode
  useEffect(() => {
    if (isDemoMode && !isExpanded) {
      console.log('🎯 [HouseholdDetailView] Auto-expanding sidebar in demo mode');
      setIsExpanded(true);
    }
  }, [isDemoMode, isExpanded]);

  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [showSymbolModal, setShowSymbolModal] = useState(false);
  
  // State for holdings and allocations
  const [rawHoldings, setRawHoldings] = useState([]);
  const [holdings, setHoldings] = useState([]);
  const [assetAllocation, setAssetAllocation] = useState({});
  const [sectorAllocation, setSectorAllocation] = useState({});
  const [performanceData, setPerformanceData] = useState({ labels: [], datasets: [] });
  const [range, setRange] = useState("all");
  
  // State for loading and error handling
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for notes
  const [notesSummary, setNotesSummary] = useState("");
  const [notesUpdates, setNotesUpdates] = useState([]);
  const [notesTasks, setNotesTasks] = useState([]);

  // Hook for fetching household holdings
  const { holdings: fetchedHoldings, loading: isLoading, error: fetchError } = useHouseholdHoldings(householdId);

  // Effect to update raw holdings when data is fetched
  useEffect(() => {
    if (fetchedHoldings) {
      setRawHoldings(fetchedHoldings);
      setLoading(false);
    }
    if (fetchError) {
      setError(fetchError);
      setLoading(false);
    }
  }, [fetchedHoldings, fetchError]);

  // Handle demo actions - ONLY navigate with proper state preservation
  const handleDemoAction = ({ type }) => {
    if (type === MODAL_TYPES.RETURN_REVIEW) {
      // Add the final line to the transcript before navigating
      const finalLine = {
        time: "13:30",
        speaker: "Mary",
        text: "That's great to hear. I hadn't realized it had grown that much."
      };
      
      console.log("🎯 Adding final line before navigation");
      addTranscriptLine(finalLine);
      
      // Navigate with state - let DemoContext handle index management
      navigate("/advisor-portal/client-database/household-profile/253/ar-profile/2025/1", {
        state: {
          resumeDemo: {
            type: 'RESUME_DEMO',
            payload: {
              transcript,
              currentIndex, // Send current index as-is
              isRecording: true,
              isPaused: false,
              expandSidebar: true,
              preserveSidebar: true
            }
          }
        }
      });
    } else {
      // Let DemoContext handle all other actions
      onAction({ type });
    }
  };

  const handleNotesSave = () => {
    console.log("Saving notes:", {
      summary: notesSummary,
      updates: notesUpdates,
      tasks: notesTasks
    });
  };

  // Market value calculation and aggregation
  useEffect(() => {
    let isCancelled = false;
    
    async function calcMarketValuesAndAggregates() {
      if (!rawHoldings || rawHoldings.length === 0) {
        if (!isCancelled) {
          setHoldings([]);
          setAssetAllocation({});
          setSectorAllocation({});
          setPerformanceData({ labels: [], datasets: [] });
        }
        return;
      }

      const symbols = Array.from(
        new Set(rawHoldings.map((h) => h.symbol).filter(Boolean))
      );

      let priceMap = {};
      let priceHistory = {};
      
      try {
        const resp = await fetch("/data/all_holdings_weekly_closes.json");
        if (isCancelled) return;
        
        const json = await resp.json();
        if (isCancelled) return;
        
        priceHistory = json;
        
        // Build O(1) lookup maps for each symbol
        const symbolPriceMaps = {};
        symbols.forEach(symbol => {
          symbolPriceMaps[symbol] = buildPriceMap(priceHistory, symbol);
          priceMap[symbol] = symbolPriceMaps[symbol].get(
            Array.from(symbolPriceMaps[symbol].keys()).pop()
          );
        });

        // Update holdings with market value
        const updatedHoldings = rawHoldings.map((h) => {
          let mv = null;
          const price = priceMap[h.symbol];
          if (price != null && !isNaN(price)) {
            mv = (parseFloat(h.quantity) || 0) * price;
          } else if (h.market_value != null && !isNaN(h.market_value)) {
            mv = parseFloat(h.market_value);
          } else {
            mv = 0;
          }
          return { ...h, market_value: mv };
        });

        if (!isCancelled) {
          setHoldings(updatedHoldings);
          
          // Aggregate asset and sector allocation
          const assetMap = {};
          const sectorMap = {};
          updatedHoldings.forEach((h) => {
            const asset = h.asset_class || "Unknown";
            const sector = h.sector || "Unknown";
            const mv = h.market_value || 0;
            assetMap[asset] = (assetMap[asset] || 0) + mv;
            sectorMap[sector] = (sectorMap[sector] || 0) + mv;
          });
          
          setAssetAllocation(assetMap);
          setSectorAllocation(sectorMap);

          // Calculate performance data with O(1) lookups
          if (symbols.length > 0 && Object.keys(priceHistory).length > 0) {
            const firstSymbol = symbols[0];
            const allDates = Array.from(symbolPriceMaps[firstSymbol].keys());
            
            // Build a map of shares by symbol
            const sharesBySymbol = {};
            updatedHoldings.forEach((h) => {
              if (h.symbol) sharesBySymbol[h.symbol] = parseFloat(h.quantity) || 0;
            });

            // Calculate portfolio value using O(1) lookups
            const values = allDates.map((date) => {
              let total = 0;
              for (const symbol in sharesBySymbol) {
                const price = symbolPriceMaps[symbol].get(date);
                const shares = sharesBySymbol[symbol];
                if (price != null) total += price * shares;
              }
              return total;
            });

            setPerformanceData({
              labels: allDates,
              datasets: [{
                label: "Portfolio Value",
                data: values,
                borderColor: "#1E3A8A",
                backgroundColor: "rgba(30,58,138,0.1)",
                fill: true,
                tension: 0.3,
                pointRadius: 3,
                pointHoverRadius: 6,
              }],
            });
          }
        }
      } catch (error) {
        console.error('Error fetching price data:', error);
        if (!isCancelled) {
          setError(error);
        }
      }
    }

    calcMarketValuesAndAggregates();
    return () => { isCancelled = true; };
  }, [rawHoldings]);

  // Group holdings by account and get account names
  const grouped = useMemo(() => {
    const map = {};
    (holdings || []).forEach((h) => {
      if (!map[h.account_number])
        map[h.account_number] = { name: h.account_name || "", rows: [] };
      map[h.account_number].name =
        h.account_name || map[h.account_number].name || "";
      map[h.account_number].rows.push(h);
    });
    return map;
  }, [holdings]);

  // Handle symbol click properly in demo mode: use Smart-Focus spotlight
  const handleSymbolClick = useCallback((symbol) => {
    if (isDemoMode) {
      // If spotlight is active, handle normal symbol click behavior instead of re-triggering spotlight
      if (activeSpotlight) {
        setSelectedSymbol(symbol);
        setShowSymbolModal(true);
        return;
      }
      // Otherwise trigger the spotlight in demo mode
      onAction({
        type: MODAL_TYPES.SMART_FOCUS,
        data: {
          spotlightId: `[data-spotlight-target="holding-${symbol}-row"]`,
          message: `Here's your ${symbol} holding—let's drill in on those numbers.`,
          demoStep: 8,
          title: "🧠 Smart Focus: Holding Details",
          description: `Let's examine the details of your ${symbol} position.`
        }
      });
    } else {
      setSelectedSymbol(symbol);
      setShowSymbolModal(true);
    }
  }, [isDemoMode, onAction, activeSpotlight, setSelectedSymbol, setShowSymbolModal]);

  // Add highlight summary handler
  const handleHighlightSummary = useCallback((summary) => {
    console.log('Highlight summary:', summary);
    // Add any highlight handling logic here
  }, []);

  // Single ref for modal content
  const modalContentRef = useRef(null);

  // Add state for current modal tab
  const [currentModalTab, setCurrentModalTab] = useState("performance");

  // Clean spotlight effect with tab support
  useLayoutEffect(() => {
    if (isDemoMode && showSymbolModal && modalContentRef.current) {
      // Wait for modal animation to complete before showing spotlight
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("spotlight", {
          detail: {
            target: modalContentRef.current,
            label: `Here's your Apple ${currentModalTab} details — feel free to explore until you click × to continue.`,
            showCloseButton: true,
            disableAutoAdvance: true,
            onClose: () => {
              // Clear spotlight immediately
              window.dispatchEvent(new CustomEvent("spotlight", { detail: null }));
              // Close modal
              setShowSymbolModal(false);
              // Wait for modal close animation before advancing demo
              setTimeout(() => {
                onAction({ type: 'ADVANCE_DEMO' });
              }, 300);
            }
          }
        }));
      }, 200);
    }
  }, [isDemoMode, showSymbolModal, onAction, currentModalTab]); // Add currentModalTab to dependencies

  // When opening the symbol modal, clear any existing spotlight immediately (row highlight)
  useEffect(() => {
    if (showSymbolModal) {
      window.dispatchEvent(new CustomEvent("spotlight", { detail: null }));
    }
  }, [showSymbolModal]);

  // Add state for expanded accounts
  const [expandedAccounts, setExpandedAccounts] = useState(new Set());

  // Set initial expanded state when grouped changes
  useEffect(() => {
    const accounts = new Set();
    Object.keys(grouped).forEach(account => accounts.add(account));
    setExpandedAccounts(accounts);
  }, [grouped]);

  // Helper to toggle account expansion
  const toggleAccountExpansion = (accountNumber, e) => {
    e.stopPropagation();
    setExpandedAccounts(prev => {
      const next = new Set(prev);
      if (next.has(accountNumber)) {
        next.delete(accountNumber);
      } else {
        next.add(accountNumber);
      }
      return next;
    });
  };

  // (timeframe pills moved inside PerformanceChart for DRYness)

  // Handle sidebar toggle with demo mode blocking
  const handleSidebarToggle = useCallback(() => {
    if (isDemoMode && isDemoLocked) {
      console.log('🎯 Sidebar toggle prevented - demo is locked');
      return;
    }
    setIsExpanded(prev => !prev);
  }, [isDemoMode, isDemoLocked]);

  return (
    <div className={`min-h-screen w-full ${isDarkMode ? 'dark' : ''}`}>
      <SubPageLayout
        title="Household Allocations"
        subtitle="View and analyze household-level portfolio allocations"
        breadcrumbItems={[
          { label: "Portfolio Management", link: "/portfolio-management" },
          {
            label: "Household Allocations",
            link: "/portfolio-management/household-allocations",
          },
        ]}
        isSidebarExpanded={isExpanded}
        loading={loading}
        error={error}
      >
        {isDemoMode && demoStep === 28 && (
          <button
            onClick={() => navigate("/advisor-portal/client-database/household-profile/253/ar-profile/2025/1", {
              state: { preserveSidebar: true }
            })}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Return to Annual Review
          </button>
        )}

        {/* Charts: 1 column on xs → 3 columns on md+, gap of 1rem */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className={`${CARD_BASE} p-4`}>
            <h4 className="font-semibold mb-3 text-xs md:text-sm text-gray-900 dark:text-white">Asset Allocation</h4>
            <div className="w-full h-48 md:h-56 lg:h-64">
              <AssetAllocationChart
                data={allocationToChartData(assetAllocation)}
                size={CHART_SIZES.sm}
                isDarkMode={isDarkMode}
              />
            </div>
          </div>
          <div className={`${CARD_BASE} p-4`}>
            <h4 className="font-semibold mb-3 text-xs md:text-sm text-gray-900 dark:text-white">Sector Allocation</h4>
            <div className="w-full h-48 md:h-56 lg:h-64">
              <SectorAllocationChart
                data={allocationToChartData(sectorAllocation, [
                  "#3B82F6", "#6366F1", "#10B981", "#F59E42", "#EF4444",
                  "#A21CAF", "#FBBF24", "#F472B6", "#6B7280", "#F87171",
                ])}
                size={CHART_SIZES.sm}
                isDarkMode={isDarkMode}
              />
            </div>
          </div>
          <div className={`${CARD_BASE} p-4`} data-demo-target="portfolio-performance">
            <h4 className="font-semibold mb-2 text-xs md:text-sm text-gray-900 dark:text-white">
              Portfolio Performance
            </h4>



            <PerformanceChart
              data={performanceData || { labels: [], datasets: [] }}
              isDarkMode={isDarkMode}
            />
          </div>
        </div>

        {/* Holdings Section - Now with responsive text and spacing */}
        <div className={CARD_BASE}>
          <h4 className="font-semibold mb-6 text-gray-900 dark:text-white">Holdings (Grouped by Account)</h4>
          {Object.keys(grouped).length === 0 ? (
            <div className="text-gray-500 dark:text-gray-500 italic text-xs">No holdings found.</div>
          ) : (
            Object.entries(grouped).map(([account, { name, rows }]) => (
              <div key={account} className="mb-4 md:mb-6">
                <div
                  className="font-semibold text-blue-700 mb-2 flex items-center justify-between cursor-pointer group text-xs md:text-sm"
                  title="View Account Details"
                  onClick={() => navigate(`/portfolio-management/account-allocations/${account}`)}
                >
                  <div className="flex items-center">
                    <button
                      className="mr-2 text-base focus:outline-none"
                      onClick={(e) => toggleAccountExpansion(account, e)}
                      aria-label={expandedAccounts.has(account) ? "Collapse" : "Expand"}
                    >
                      {expandedAccounts.has(account) ? "▼" : "▶"}
                    </button>
                    <span className="group-hover:underline" style={{ textDecoration: "underline dotted" }}>
                      {(() => {
                        const holding = rows && rows.length > 0 ? rows[0] : null;
                        const displayName = getAccountDisplayName(holding);
                        return displayName ? `${displayName} (${account})` : `Account (${account})`;
                      })()}
                    </span>
                  </div>
                  <div className="text-gray-600 dark:text-gray-400 text-xs">
                    Total Market Value: ${rows.reduce((sum, row) => sum + (row.market_value || 0), 0).toLocaleString()}
                  </div>
                </div>
                {expandedAccounts.has(account) && (
                  <HoldingsTable
                    data={rows}
                    columns={[
                      {
                        Header: "Symbol",
                        accessor: "symbol",
                        Cell: (row) => (
                          <span
                            className="text-blue-600 underline cursor-pointer hover:text-blue-800 text-xs md:text-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSymbolClick(row.symbol);
                            }}
                            data-spotlight-target={`holding-${row.symbol}-row`}
                          >
                            {row.symbol}
                          </span>
                        ),
                      },
                      { Header: "Asset Class", accessor: "asset_class" },
                      { Header: "Sector", accessor: "sector" },
                      { Header: "Shares", accessor: "quantity" },
                      {
                        Header: "Market Value",
                        accessor: "market_value",
                        Cell: (row) => row.market_value ? `$${row.market_value.toLocaleString()}` : "-",
                      },
                      { Header: "Last Trade Date", accessor: "as_of_date" },
                    ]}
                    onRowClick={(row) => {
                      if (isDemoMode && activeSpotlight) return;
                      if (isDemoMode) {
                        handleSymbolClick(row.symbol);
                      } else {
                        setSelectedSymbol(row.symbol);
                        setShowSymbolModal(true);
                      }
                    }}
                    tableClassName={TABLE_BASE}
                    headerClassName={TABLE_VARIANTS.dense.header}
                    cellClassName={TABLE_VARIANTS.dense.cell}
                  />
                )}
              </div>
            ))
          )}
        </div>
      </SubPageLayout>

      <ThemeProvider theme={{ ...theme, isDarkMode }}>
        <SidebarWrapper $demoActive={isDemoMode && isDemoLocked}>
          <SidebarTemplate
            isExpanded={isExpanded}
            onToggle={handleSidebarToggle}
            disabled={false}
            transcript={transcriptSlice}
            isRecording={isRecording}
            setIsRecording={setIsRecording}
            isPaused={isPaused}
            setIsPaused={setIsPaused}
            notesSummary={notesSummary}
            onNotesSummaryChange={setNotesSummary}
            notesUpdates={notesUpdates}
            onNotesUpdatesChange={setNotesUpdates}
            notesTasks={notesTasks}
            onNotesTasksChange={setNotesTasks}
            onNotesSave={handleNotesSave}
            onHighlightSummary={handleHighlightSummary}
            key={`sidebar-${currentIndex}`}
          />
        </SidebarWrapper>

        <SymbolDetailModal
          ref={modalContentRef}
          symbol={selectedSymbol}
          open={showSymbolModal}
          onClose={() => {
            window.dispatchEvent(new CustomEvent("spotlight", { detail: null }));
            setShowSymbolModal(false);
          }}
          onTabChange={setCurrentModalTab}
        />
      </ThemeProvider>
    </div>
  );
};

export default HouseholdDetailView;